import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './BpsBillingDetail.module.scss';
import { BuyElectricityNavigationState, BuyElectricityPurchaseType } from '../../../../buy-electricity/buy-electricity.types';
import { FileIcon } from '../../../../../../assets/icons';
import { ApplicationRoutes } from '../../../../../../routes';

export enum BpsBillingNotificationType {
    Unknown = 0,
    BillIssued = 1,
    BillRecalled = 2,
    BillReissued = 3,
    FraudBillIssued = 4,
    AdjustmentApplied = 5,
    PaymentReceived = 21,
    NoPaymentAfter14Days = 22,
    NoPaymentAfter21Days = 23,
    DueForDisconnection = 41,
    ScheduledForDisconnection = 42,
    Disconnected = 43,
    Reconnected = 44,
    ReconnectionBill = 51,
    WarningOn26th = 61,
}

interface BpsBillingNotificationItem {
    notificationType: BpsBillingNotificationType;
    accountNumber: string;
    referenceNumber?: string;
    amount?: number;
    endDate?: string;
    dueDate?: string;
    isPaymentReminderEscalation: boolean;
}

interface BpsBillingContext {
    bpsBillingNotificationItem?: BpsBillingNotificationItem;
}

interface BpsBillingDetailProps {
    context: BpsBillingContext;
    notificationId: string;
    title: string;
    message: string;
}

const ordinal = (n: number): string => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
};

const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${ordinal(date.getDate())} ${date.toLocaleDateString('en-US', { month: 'short' })} ${date.getFullYear()}`;
};

const formatAmount = (amount: number): string =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const typeLabel: Record<BpsBillingNotificationType, string> = {
    [BpsBillingNotificationType.Unknown]: 'Unknown',
    [BpsBillingNotificationType.BillIssued]: 'New Bill Issued',
    [BpsBillingNotificationType.BillRecalled]: 'Bill Recalled',
    [BpsBillingNotificationType.BillReissued]: 'Bill Reissued',
    [BpsBillingNotificationType.FraudBillIssued]: 'Fraud Bill Issued',
    [BpsBillingNotificationType.AdjustmentApplied]: 'Adjustment Applied',
    [BpsBillingNotificationType.PaymentReceived]: 'Payment Received',
    [BpsBillingNotificationType.NoPaymentAfter14Days]: 'No Payment After 14 Days',
    [BpsBillingNotificationType.NoPaymentAfter21Days]: 'No Payment After 21 Days',
    [BpsBillingNotificationType.DueForDisconnection]: 'Due for Disconnection',
    [BpsBillingNotificationType.ScheduledForDisconnection]: 'Scheduled for Disconnection',
    [BpsBillingNotificationType.Disconnected]: 'Disconnected',
    [BpsBillingNotificationType.Reconnected]: 'Reconnected',
    [BpsBillingNotificationType.ReconnectionBill]: 'Reconnection Bill',
    [BpsBillingNotificationType.WarningOn26th]: 'Warning',
};

const PAYMENT_REQUIRED_TYPES: BpsBillingNotificationType[] = [
    BpsBillingNotificationType.BillIssued,
    BpsBillingNotificationType.BillReissued,
    BpsBillingNotificationType.FraudBillIssued, //exact
    BpsBillingNotificationType.AdjustmentApplied,
    BpsBillingNotificationType.NoPaymentAfter14Days,
    BpsBillingNotificationType.NoPaymentAfter21Days,
    BpsBillingNotificationType.DueForDisconnection,
    BpsBillingNotificationType.ScheduledForDisconnection,
    BpsBillingNotificationType.Disconnected, //exact
    BpsBillingNotificationType.WarningOn26th,
];

const BpsBillingDetail: React.FC<BpsBillingDetailProps> = ({ context, notificationId, title, message }) => {
    const item = context.bpsBillingNotificationItem;
    const navigate = useNavigate();

    if (!item) return null;

    const requiresPayment = PAYMENT_REQUIRED_TYPES.includes(item.notificationType);

    const exactPayment = item.notificationType === BpsBillingNotificationType.FraudBillIssued || item.notificationType === BpsBillingNotificationType.Disconnected;

    const isDisconnectionWarning = [
        BpsBillingNotificationType.DueForDisconnection,
        BpsBillingNotificationType.ScheduledForDisconnection,
        BpsBillingNotificationType.Disconnected,
        BpsBillingNotificationType.WarningOn26th,
        BpsBillingNotificationType.NoPaymentAfter14Days,
        BpsBillingNotificationType.NoPaymentAfter21Days,
    ].includes(item.notificationType);

    const isPositive = [
        BpsBillingNotificationType.PaymentReceived,
        BpsBillingNotificationType.Reconnected,
    ].includes(item.notificationType);

    const amountClass = isPositive
        ? styles.rowValueCredited
        : isDisconnectionWarning
        ? styles.rowValueDebited
        : styles.rowValue;

   

    return (
        <div className={styles.container}>
            <div className={styles.headerCard}>
                <div className={styles.iconCircle}>
                    <FileIcon />
                </div>
                <div className={styles.headerText}>
                    <h3 className={styles.headerTitle}>{title}</h3>
                    {message && <p className={styles.headerDesc}>{message}</p>}
                </div>
            </div>

            <div className={styles.infoBlock}>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Type</span>
                    <span className={styles.rowValue}>{typeLabel[item.notificationType]}</span>
                </div>

                <div className={styles.row}>
                    <span className={styles.rowLabel}>Account Number</span>
                    <span className={styles.rowValue}>{item.accountNumber}</span>
                </div>

                {item.referenceNumber && (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Reference Number</span>
                        <span className={styles.rowValue}>{item.referenceNumber}</span>
                    </div>
                )}

                {item.amount != null && (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Amount</span>
                        <span className={amountClass}>₦ {formatAmount(item.amount)}</span>
                    </div>
                )}

                {item.dueDate && (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Due Date</span>
                        <span className={styles.rowValue}>{formatDate(item.dueDate)}</span>
                    </div>
                )}

                {item.endDate && (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>End Date</span>
                        <span className={styles.rowValue}>{formatDate(item.endDate)}</span>
                    </div>
                )}

                {item.isPaymentReminderEscalation && (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Escalation</span>
                        <span className={styles.rowValueDebited}>Payment reminder escalation</span>
                    </div>
                )}


            </div>
            {
                requiresPayment &&  <div className={styles.btnContainer}>
                    <button
                        className={styles.btn}
                        onClick={() => navigate(`/${ApplicationRoutes.BUY_ELECTRICITY}`, {
                            state: {
                                meterNumber: item.accountNumber,
                                amountToPay: item.amount?.toString() ?? '',
                                paymentType: 'postpaid',
                                purchaseType: exactPayment ? BuyElectricityPurchaseType.Exact : BuyElectricityPurchaseType.Partial,
                                billingDashNotificationId: notificationId,
                            } satisfies BuyElectricityNavigationState
                        })}
                    >
                        Pay Now
                    </button>
                </div>
            }
        </div>
    );
};

export default BpsBillingDetail;
